import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 starfield relative">
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 w-full max-w-md glass-strong rounded-2xl p-8 sm:p-10">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <Sparkles className="w-6 h-6 text-gold" />
          <span className="font-serif text-2xl text-slate-50">Cosomic Sage</span>
        </Link>
        <h1 className="font-serif text-3xl text-center text-slate-50 mb-1">Welcome back</h1>
        <p className="text-center text-slate-400 text-sm mb-8">Sign in to continue your cosmic journey</p>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-slate-300">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              required
              className="mt-1.5 bg-slate-900/50 border-white/10 focus:border-amber-500 focus:ring-amber-500 text-white"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-slate-300">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              required
              className="mt-1.5 bg-slate-900/50 border-white/10 focus:border-amber-500 focus:ring-amber-500 text-white"
              placeholder="••••••••"
            />
          </div>
          {error && <div className="text-sm text-rose-400" data-testid="login-error">{error}</div>}
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium py-5"
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>
        </form>

        <div className="flex items-center my-6 gap-3">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-slate-500">OR</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <Button
          onClick={handleGoogle}
          variant="outline"
          data-testid="login-google-button"
          className="w-full rounded-full border-white/15 text-slate-200 hover:bg-white/5 hover:text-amber-300 py-5"
        >
          Continue with Google
        </Button>

        <p className="text-center text-sm text-slate-400 mt-6">
          New here?{" "}
          <Link to="/register" className="text-amber-400 hover:text-amber-300" data-testid="login-to-register-link">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
